import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChevronRight, ChevronLeft, Sun, Wind, Zap, Cable, MapPin, Euro, Calendar, User, Mail, Phone, CheckCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const EstimationForm = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: "",
    surface: "",
    budget: "",
    timeline: "",
    postalCode: "",
    name: "",
    email: "",
    phone: "",
  });

  const services = [
    { id: "photovoltaique", label: "Photovoltaïque", icon: Sun },
    { id: "climatisation", label: "Climatisation", icon: Wind },
    { id: "energie", label: "Contrats Énergie", icon: Zap },
    { id: "fibre", label: "Fibre Optique", icon: Cable },
  ];

  const budgets = [
    { id: "0-5k", label: "0 - 5 000€" },
    { id: "5k-10k", label: "5 000 - 10 000€" },
    { id: "10k-20k", label: "10 000 - 20 000€" },
    { id: "20k+", label: "Plus de 20 000€" },
  ];

  const timelines = [
    { id: "urgent", label: "Urgent (< 1 mois)" },
    { id: "1-3mois", label: "1 à 3 mois" },
    { id: "3-6mois", label: "3 à 6 mois" },
    { id: "6mois+", label: "Plus de 6 mois" },
  ];

  const handleNext = () => {
    if (step < 6) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/submissions/estimation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_type: formData.projectType,
          surface: formData.surface,
          budget: formData.budget,
          timeline: formData.timeline,
          postal_code: formData.postalCode,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        }),
      });

      if (response.ok) {
        toast({
          title: "Estimation envoyée !",
          description: "Nous vous contacterons dans les 24h.",
        });
        // Reset form
        setFormData({
          projectType: "",
          surface: "",
          budget: "",
          timeline: "",
          postalCode: "",
          name: "",
          email: "",
          phone: "",
        });
        setStep(1);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error submitting estimation:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.projectType !== "";
      case 2:
        return formData.surface !== "";
      case 3:
        return formData.budget !== "";
      case 4:
        return formData.timeline !== "";
      case 5:
        return formData.postalCode !== "";
      case 6:
        return formData.name !== "" && formData.email !== "" && formData.phone !== "";
      default:
        return false;
    }
  };

  const progressPercentage = (step / 6) * 100;

  return (
    <section id="estimation" className="py-24 bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            💰 Découvrez combien vous pouvez <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">économiser en 2 min</span>
          </h2>
          <p className="text-xl text-gray-300 mb-4">
            <strong className="text-emerald-400">100% Gratuit</strong> • Sans engagement • Réponse en 24h maximum
          </p>
          <div className="inline-flex items-center space-x-2 bg-orange-500/20 border border-orange-400 rounded-full px-6 py-2 text-orange-300">
            <span className="text-sm font-semibold">⚡ Plus de 500 devis réalisés ce mois-ci</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Étape {step} sur 6</span>
            <span className="text-sm text-gray-400">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        <Card className="border-0 shadow-2xl bg-white/5 backdrop-blur-lg">
          <CardContent className="p-8 md:p-12">
            {/* Step 1: Project Type */}
            {step === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">Quel type de projet souhaitez-vous réaliser ?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <button
                        key={service.id}
                        onClick={() => setFormData({ ...formData, projectType: service.id })}
                        className={`p-6 rounded-xl border-2 transition-all text-left ${
                          formData.projectType === service.id
                            ? "border-blue-500 bg-blue-500/20"
                            : "border-gray-600 hover:border-blue-400 bg-white/5"
                        }`}
                      >
                        <Icon className="mb-3" size={32} />
                        <p className="font-semibold text-lg">{service.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Surface */}
            {step === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Quelle est la surface concernée ?
                </h3>
                <div className="space-y-4">
                  <Input
                    type="number"
                    placeholder="Entrez la surface en m²"
                    value={formData.surface}
                    onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                    className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400 text-lg py-6"
                  />
                  <p className="text-gray-400 text-sm">Cette information nous aide à dimensionner votre installation</p>
                </div>
              </div>
            )}

            {/* Step 3: Budget */}
            {step === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">Quel est votre budget estimé ?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {budgets.map((budget) => (
                    <button
                      key={budget.id}
                      onClick={() => setFormData({ ...formData, budget: budget.id })}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        formData.budget === budget.id
                          ? "border-blue-500 bg-blue-500/20"
                          : "border-gray-600 hover:border-blue-400 bg-white/5"
                      }`}
                    >
                      <Euro className="mb-3" size={24} />
                      <p className="font-semibold text-lg">{budget.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Timeline */}
            {step === 4 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">Quel est votre délai souhaité ?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {timelines.map((timeline) => (
                    <button
                      key={timeline.id}
                      onClick={() => setFormData({ ...formData, timeline: timeline.id })}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        formData.timeline === timeline.id
                          ? "border-blue-500 bg-blue-500/20"
                          : "border-gray-600 hover:border-blue-400 bg-white/5"
                      }`}
                    >
                      <Calendar className="mb-3" size={24} />
                      <p className="font-semibold text-lg">{timeline.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 5: Location */}
            {step === 5 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">Où se situe votre projet ?</h3>
                <div className="space-y-4">
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="text"
                      placeholder="Code postal"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400 text-lg py-6 pl-12"
                      maxLength={5}
                    />
                  </div>
                  <p className="text-gray-400 text-sm">Nous vérifions que nous intervenons dans votre zone</p>
                </div>
              </div>
            )}

            {/* Step 6: Contact Info */}
            {step === 6 && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-2xl font-bold text-white mb-6">Comment pouvons-nous vous contacter ?</h3>
                <div className="space-y-4">
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="text"
                      placeholder="Nom complet"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400 text-lg py-6 pl-12"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400 text-lg py-6 pl-12"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="tel"
                      placeholder="Téléphone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400 text-lg py-6 pl-12"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-700">
              <Button
                onClick={handlePrev}
                variant="outline"
                disabled={step === 1}
                className="border-gray-600 text-white hover:bg-white/10"
              >
                <ChevronLeft className="mr-2" size={20} />
                Retour
              </Button>
              {step < 6 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white border-0"
                >
                  Suivant
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isStepValid()}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
                >
                  <CheckCircle className="mr-2" size={20} />
                  Obtenir mon estimation
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EstimationForm;